import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { gtmPush } from './gtm';
import { clearRegionCache } from './region';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try { return JSON.parse(localStorage.getItem('ac_user') || 'null'); }
        catch { return null; }
    });
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data);
            localStorage.setItem('ac_user', JSON.stringify(data));
            // Round 40n — bust the region cache whenever we refresh
            // the user. /auth/me may surface a residence_country that
            // wasn't known on the previous render (e.g. just set by a
            // PayPal capture or admin tooling). Cheap; idempotent.
            clearRegionCache();
            // A31 — sync the user's display timezone if it changed (or was
            // never captured). Sent best-effort; ignored on failure.
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (tz && tz !== data?.display_timezone) {
                    await api.put('/auth/me', { display_timezone: tz });
                }
            } catch {
                /* ignore */
            }
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        // A94 — If we're returning from Emergent Google OAuth, skip the
        // /auth/me refresh. AuthCallback will exchange the session_id
        // and seed both localStorage + setUser before navigating to /app.
        // Per the Emergent Auth Integration Playbook: this prevents a
        // race where the initial /auth/me fires with a stale token
        // before the callback can complete.
        if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
            return;
        }
        if (localStorage.getItem('ac_token')) refresh();
    }, [refresh]);

    const login = async (email, password) => {
        setLoading(true);
        try {
            const { data } = await api.post('/auth/login', { email, password });
            localStorage.setItem('ac_token', data.token);
            localStorage.setItem('ac_user', JSON.stringify(data.user));
            // Round 40n — bust the region cache the moment the token
            // changes. The anon visitor's region (from IP detection)
            // is no longer valid; the very next fetchRegion() call
            // will hit /region/me with auth and pick up the user's
            // persisted residence_country.
            clearRegionCache();
            setUser(data.user);
            // A31 — capture browser timezone right after login.
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (tz) await api.put('/auth/me', { display_timezone: tz });
            } catch { /* ignore */ }
            return data.user;
        } finally { setLoading(false); }
    };

    const signup = async (email, password, name, extras = {}) => {
        setLoading(true);
        try {
            // Round 25 — signup now returns { verification_required: true, email }.
            // The /verify endpoint returns the actual session token. Caller
            // (Signup.jsx) routes the user to /verify after this resolves.
            // Round 32 — if a referral code was captured from ?ref= on the
            // landing URL, send it with signup so the referral row is created.
            // Round 40o — `extras` carries the optional phone_e164 +
            // phone_country fields collected on the signup page.
            const referral_code = (localStorage.getItem('ac_ref') || '').trim() || undefined;
            const { data } = await api.post('/auth/signup', {
                email, password, name, referral_code, ...extras,
            });
            return { verificationRequired: true, email: data.email || email };
        } finally { setLoading(false); }
    };

    // Round 25 — finalise signup (or unverified-login) by submitting the
    // OTP. Returns the activated user.
    const verify = async (email, code) => {
        setLoading(true);
        try {
            const { data } = await api.post('/auth/verify', { email, code });
            // GTM — fire `signup` event the moment the new account is
            // actually activated (account creation in this app is a
            // two-step flow: /auth/signup queues an OTP, /auth/verify
            // mints the session token). `gtmPush` is a no-op outside
            // the production hostname allow-list so staging signups
            // never pollute production analytics.
            gtmPush({
                event: 'signup',
                user_id: data.user?.id,
                method: 'email',
            });
            localStorage.setItem('ac_token', data.token);
            localStorage.setItem('ac_user', JSON.stringify(data.user));
            // Round 40n — see comment in login(). Same rationale —
            // signup/verify also flips the token; the anon-IP region
            // cache must not carry over to the freshly-verified user.
            clearRegionCache();
            setUser(data.user);
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (tz) await api.put('/auth/me', { display_timezone: tz });
            } catch { /* ignore */ }
            return data.user;
        } finally { setLoading(false); }
    };

    const resendCode = async (email) => {
        await api.post('/auth/resend-code', { email });
    };

    const logout = () => {
        localStorage.removeItem('ac_token');
        localStorage.removeItem('ac_user');
        // Round 40n — and forget the region too; if someone else logs
        // in on this browser they must not inherit the prior user's
        // currency.
        clearRegionCache();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, login, signup, verify, resendCode, logout, refresh, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
